import { Alert, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormControls,
  FormikArray,
  FormikNumericField,
  FormikSelect,
  P2PProviderSelect,
  TraderSelect,
} from 'components';
import { OrderType } from 'enums';
import { TranslationNamespace } from 'i18n';
import { OrderAssigneeType } from 'types';

import { Values } from '../ManageCascade';

type AssigneeItemProps = {
  item: { assigneeType: OrderAssigneeType };
  index: number;
};

const AssigneeItem: React.FC<AssigneeItemProps> = ({ item, index }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.cascades.manage',
  });
  const formik = useFormikContext<Values>();

  const assigneeTypeOptions = useMemo(
    () => [
      { value: OrderAssigneeType.Trader, label: t('assignee_types.trader') },
      {
        value: OrderAssigneeType.P2PProvider,
        label: t('assignee_types.p2p_provider'),
      },
      {
        value: OrderAssigneeType.AnyTrader,
        label: t('assignee_types.any_trader'),
      },
    ],
    [t],
  );

  const handleAssigneeTypeChange = useCallback(() => {
    formik.setFieldValue(`payoutOrderAssignees[${index}].traderId`, '');
    formik.setFieldValue(`payoutOrderAssignees[${index}].p2pProviderId`, '');
  }, [formik, index]);

  const showProviderTimeoutWarning = useMemo(() => {
    const assignee = formik.values.payoutOrderAssignees?.[index];
    if (!assignee?.timeout) {
      return false;
    }

    const providerTimeout = assignee?.p2pProvider?.payoutConfig?.orderTimeout;
    if (!providerTimeout) {
      return false;
    }

    return assignee?.timeout > providerTimeout;
  }, [formik.values.payoutOrderAssignees, index]);

  return (
    <FormControls sx={{ width: '100%' }}>
      <FormControls row>
        <FormikSelect
          name={`payoutOrderAssignees[${index}].assigneeType`}
          label={t('fields.assignee_type')}
          options={assigneeTypeOptions}
          sx={{ flex: 2 }}
          onChange={handleAssigneeTypeChange}
        />
        <FormikNumericField
          name={`payoutOrderAssignees[${index}].timeout`}
          label={t('fields.timeout')}
          min={0}
          decimalScale={0}
          allowNegative={false}
          required
          sx={{ flex: 1 }}
        />
      </FormControls>

      {item.assigneeType === OrderAssigneeType.P2PProvider && (
        <Fragment>
          <P2PProviderSelect
            name={`payoutOrderAssignees[${index}].p2pProviderId`}
            label={t('fields.p2p_provider')}
            ignoreTradeMethodsIds
            hideArchived
            direction={OrderType.Payout}
            sx={{ flex: 1 }}
          />
          {showProviderTimeoutWarning && (
            <Alert severity="warning">
              <Typography variant="body2">
                {t('alerts.p2p_provider_timeout_warning')}
              </Typography>
            </Alert>
          )}
        </Fragment>
      )}
      {item.assigneeType === OrderAssigneeType.Trader && (
        <TraderSelect
          name={`payoutOrderAssignees[${index}].traderId`}
          label={t('fields.trader')}
          sx={{ flex: 1 }}
          noneOption={false}
        />
      )}
    </FormControls>
  );
};

export const CascadeTimeoutAssignees: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.cascades.manage',
  });

  return (
    <Fragment>
      <Typography variant="subtitle1">
        {t('sections.payout_order_assignees.title')}
      </Typography>
      <FormikArray
        addItemLabel={t('buttons.add_payout_order_assignee')}
        name="payoutOrderAssignees"
        sortable
        removable
        defaultValue={{
          assigneeType: OrderAssigneeType.Trader,
          traderId: '',
          p2pProviderId: '',
          timeout: 10,
        }}
        ItemComponent={AssigneeItem}
      />
    </Fragment>
  );
};
